import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Badge, Button, FormField, Heading, Input, Text } from "@lendcord/concord";
import { useGetProfileQuery, useUpsertAddressMutation } from "@/app/profile/api/profile.api";
import { PINCODE_REGEX } from "@/app/profile/validators";
import type { Address, AddressType } from "@/app/profile/profile.types";
import { AddressesSectionSkeleton } from "./AddressesSectionSkeleton";

const ADDRESS_TYPES: AddressType[] = ["CURRENT", "PERMANENT", "OFFICE"];

const ADDRESS_TYPE_LABEL_IDS: Record<AddressType, string> = {
  CURRENT: "lender.profile.addressTypeCurrent",
  PERMANENT: "lender.profile.addressTypePermanent",
  OFFICE: "lender.profile.addressTypeOffice",
};

interface AddressFormState {
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
}

const EMPTY_FORM: AddressFormState = { line1: "", line2: "", city: "", state: "", pincode: "" };

interface AddressesSectionProps {
  addresses: Address[] | null;
}

export function AddressesSection({ addresses }: AddressesSectionProps) {
  const intl = useIntl();
  const { refetch } = useGetProfileQuery();
  const [upsertAddress] = useUpsertAddressMutation();

  const [editingType, setEditingType] = useState<AddressType | null>(null);
  const [form, setForm] = useState<AddressFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof AddressFormState, string>>>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!addresses) {
    return <AddressesSectionSkeleton />;
  }

  function handleStartEdit(type: AddressType, existing: Address | undefined) {
    setForm(
      existing
        ? {
            line1: existing.line1,
            line2: existing.line2 ?? "",
            city: existing.city,
            state: existing.state,
            pincode: existing.pincode,
          }
        : EMPTY_FORM
    );
    setErrors({});
    setFormError(undefined);
    setEditingType(type);
  }

  function handleCancel() {
    setEditingType(null);
  }

  async function handleSubmit(event: FormEvent, type: AddressType) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof errors = {};
    if (!form.line1.trim()) {
      nextErrors.line1 = intl.formatMessage({ id: "lender.profile.addressLine1RequiredError" });
    }
    if (!form.city.trim()) {
      nextErrors.city = intl.formatMessage({ id: "lender.profile.addressCityRequiredError" });
    }
    if (!form.state.trim()) {
      nextErrors.state = intl.formatMessage({ id: "lender.profile.addressStateRequiredError" });
    }
    if (!PINCODE_REGEX.test(form.pincode)) {
      nextErrors.pincode = intl.formatMessage({ id: "lender.profile.addressPincodeFormatError" });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await upsertAddress({
        type,
        payload: {
          line1: form.line1.trim(),
          line2: form.line2.trim() || undefined,
          city: form.city.trim(),
          state: form.state.trim(),
          pincode: form.pincode,
        },
      }).unwrap();
      await refetch();
      setEditingType(null);
    } catch {
      setFormError(intl.formatMessage({ id: "lender.profile.addressUpdateError" }));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Heading level={3}>
        <FormattedMessage id="lender.profile.addressesHeading" />
      </Heading>

      <div className="flex flex-col gap-4">
        {ADDRESS_TYPES.map((type) => {
          const existing = addresses.find((address) => address.type === type);

          if (editingType === type) {
            return (
              <form
                key={type}
                onSubmit={(event) => handleSubmit(event, type)}
                noValidate
                className="flex flex-col gap-4 rounded-md border border-border p-4"
              >
                <Badge status="neutral">{intl.formatMessage({ id: ADDRESS_TYPE_LABEL_IDS[type] })}</Badge>

                {formError && <AlertBanner status="error">{formError}</AlertBanner>}

                <FormField
                  label={intl.formatMessage({ id: "lender.profile.addressLine1Label" })}
                  error={errors.line1}
                  required
                >
                  <Input value={form.line1} onChange={(event) => setForm({ ...form, line1: event.target.value })} />
                </FormField>

                <FormField label={intl.formatMessage({ id: "lender.profile.addressLine2Label" })}>
                  <Input value={form.line2} onChange={(event) => setForm({ ...form, line2: event.target.value })} />
                </FormField>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    label={intl.formatMessage({ id: "lender.profile.addressCityLabel" })}
                    error={errors.city}
                    required
                  >
                    <Input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
                  </FormField>
                  <FormField
                    label={intl.formatMessage({ id: "lender.profile.addressStateLabel" })}
                    error={errors.state}
                    required
                  >
                    <Input value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} />
                  </FormField>
                </div>

                <FormField
                  label={intl.formatMessage({ id: "lender.profile.addressPincodeLabel" })}
                  error={errors.pincode}
                  required
                >
                  <Input
                    value={form.pincode}
                    onChange={(event) => setForm({ ...form, pincode: event.target.value.replace(/\D/g, "") })}
                    maxLength={6}
                  />
                </FormField>

                <div className="flex gap-3">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <FormattedMessage id="lender.profile.savingButton" />
                    ) : (
                      <FormattedMessage id="lender.profile.saveButton" />
                    )}
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
                    <FormattedMessage id="lender.profile.cancelButton" />
                  </Button>
                </div>
              </form>
            );
          }

          return (
            <div key={type} className="rounded-md border border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <Badge status="neutral">{intl.formatMessage({ id: ADDRESS_TYPE_LABEL_IDS[type] })}</Badge>
                <Button
                  type="button"
                  variant="ghost"
                  density="compact"
                  onClick={() => handleStartEdit(type, existing)}
                >
                  <FormattedMessage id={existing ? "lender.profile.editButton" : "lender.profile.addButton"} />
                </Button>
              </div>

              {existing ? (
                <>
                  <Text as="p" size="sm" className="mt-2 font-medium">
                    {existing.line1}
                    {existing.line2 ? `, ${existing.line2}` : ""}
                  </Text>
                  <Text as="p" size="sm" variant="muted">
                    {existing.city}, {existing.state} – {existing.pincode}, {existing.country}
                  </Text>
                </>
              ) : (
                <Text as="p" size="sm" variant="muted" className="mt-2">
                  <FormattedMessage id="lender.profile.noAddressMessage" />
                </Text>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
